import React from 'react';
import { useTranslation } from 'react-i18next';
import { selectFieldStyles as styles } from '../styles/commonStyles';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  placeholder?: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | null;
  /** Extra style merged onto the field container (e.g. flex sizing inside a row). */
  containerStyle?: React.CSSProperties;
  /** Omit the empty placeholder option (for selects that always hold a value, e.g. a default). */
  hidePlaceholder?: boolean;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  value,
  onChange,
  options,
  label,
  placeholder,
  id,
  required = false,
  disabled = false,
  error = null,
  containerStyle,
  hidePlaceholder = false,
}) => {
  const { t } = useTranslation();
  return (
    <div style={{ ...styles.field, ...containerStyle }}>
      {label && (
        <label style={styles.label} htmlFor={id}>
          {label}{required && <span style={styles.required}> *</span>}
        </label>
      )}
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-error={error ? 'true' : undefined}
        style={{
          ...styles.select,
          ...(disabled ? styles.selectDisabled : {}),
          ...(error ? styles.selectError : {}),
        }}
      >
        {!hidePlaceholder && <option value="">{placeholder ?? t('common.select', 'Selecteer…')}</option>}
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)} disabled={o.disabled}>{o.label}</option>
        ))}
      </select>
      {error && <p style={styles.errorMessage}>{error}</p>}
    </div>
  );
};

export default SelectField;
